/**
 * How long a delivery may stay open before it is late.
 *
 * The product had no deadline anywhere, so "overdue" could only ever have been
 * a number somebody typed into the code — and a number in the code would have
 * been one country's working day imposed on every business using CenoX. This
 * screen is where a business states its own, and nothing else decides it.
 *
 * The most specific deadline wins, the same way a price does: service type in
 * a state, then service type in a country, then service type anywhere, then
 * the business-wide fallback. A business that sets none is not broken — the
 * board then reports how long each job has been open and declines to call any
 * of them late, because nobody has said what late means here.
 *
 * Not dated, unlike prices. A price has to answer "what was this checked
 * against in March"; a deadline answers "is this job late now", so editing one
 * is meant to change what the board says today.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DELIVERY_SERVICE_TYPES,
  findCountry,
  findState,
  openForLabel,
  statesOf,
  type SlaRuleView,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Spinner,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';

/** Turn an API error's per-field messages into one message per field. */
function firstFieldErrors(caught: ApiError): Record<string, string> {
  return Object.fromEntries(
    Object.entries(caught.fields ?? {}).map(([k, v]) => [k, v[0] ?? '']),
  );
}

/** What a rule covers, in words, so the precedence is readable down the column. */
export function scopeLabel(rule: SlaRuleView): string {
  const where =
    rule.stateCode && rule.countryCode
      ? `${findState(rule.countryCode, rule.stateCode)?.name ?? rule.stateCode}, ` +
        `${findCountry(rule.countryCode)?.name ?? rule.countryCode}`
      : rule.countryCode
        ? (findCountry(rule.countryCode)?.name ?? rule.countryCode)
        : 'Everywhere';
  return `${rule.serviceType ?? 'Every job'} · ${where}`;
}

/**
 * How specific a rule is, for display order — the narrowest statement first,
 * which is also the order they are applied in.
 */
function rank(rule: SlaRuleView): number {
  return (
    (rule.serviceType !== null ? 4 : 0) +
    (rule.countryCode !== null ? 2 : 0) +
    (rule.stateCode !== null ? 1 : 0)
  );
}

export function DeadlinesPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const mayManage = can('delivery_pricing:manage');

  const [form, setForm] = useState({
    serviceType: '',
    countryCode: '',
    stateCode: '',
    hours: '',
    note: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const set =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((f) => ({
        ...f,
        [key]: event.target.value,
        // A state belongs to a country; changing the country drops the state
        // rather than leaving "EN" attached to Ghana.
        ...(key === 'countryCode' ? { stateCode: '' } : {}),
      }));

  const query = useQuery({
    queryKey: ['delivery-sla-rules', business?.businessId],
    queryFn: () => api.get<{ data: SlaRuleView[] }>(path('/deliveries/sla-rules')),
    // Not asked for at all by somebody who may not set them: the screen has
    // nothing to show them either way.
    enabled: Boolean(business) && mayManage,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-sla-rules'] });
    // The board's Active card counts what is overdue, so it changes with this.
    void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.put(path('/deliveries/sla-rules'), {
        serviceType: form.serviceType || null,
        countryCode: form.countryCode || null,
        stateCode: form.stateCode || null,
        hours: Number(form.hours),
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      }),
    onSuccess: () => {
      setForm((f) => ({ ...f, hours: '', note: '' }));
      refresh();
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(firstFieldErrors(caught));
      } else {
        setError('Could not save this deadline.');
      }
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(path(`/deliveries/sla-rules/${id}`)),
    onSuccess: refresh,
    onError: (caught) =>
      setListError(
        caught instanceof ApiError ? caught.message : 'Could not remove that deadline.',
      ),
  });

  if (!mayManage) {
    return (
      <>
        <PageHeader title="Delivery deadlines" />
        <Card>
          <EmptyState
            title="Deadlines are set by the owner"
            description="Ask the owner or a manager to change how long a job may stay open."
          />
        </Card>
      </>
    );
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  const rules = [...(query.data?.data ?? [])].sort((a, b) => rank(b) - rank(a));
  const states = statesOf(form.countryCode);
  const fallback = rules.find((r) => rank(r) === 0);

  return (
    <>
      <PageHeader
        title="Delivery deadlines"
        description="How long a job may stay open before the board marks it overdue."
      />

      <Card>
        <CardHeader
          title="Your deadlines"
          action={
            fallback ? (
              <span className="text-xs text-slate-500">
                Anything not listed: {openForLabel(fallback.hours)}
              </span>
            ) : undefined
          }
        />
        {listError && (
          <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{listError}</p>
          </div>
        )}
        {query.isLoading && <Spinner label="Loading deadlines" />}
        {query.isError && <ErrorState message="Could not load deadlines." />}
        {query.data && rules.length === 0 && (
          <EmptyState
            title="No deadlines set"
            description={
              'Until you set one, the board shows how long each job has been open and ' +
              'does not call any of them overdue.'
            }
          />
        )}
        {rules.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3">Applies to</th>
                  <th className="py-2 pr-3">Deadline</th>
                  <th className="py-2 pr-3">Note</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 align-top">
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      {scopeLabel(r)}
                      {rank(r) === 0 && (
                        <span className="ml-2">
                          <Badge tone="info">Fallback</Badge>
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                      {openForLabel(r.hours)}
                      <div className="text-xs text-slate-500">{r.hours}h</div>
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{r.note ?? '—'}</td>
                    <td className="py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setListError(null);
                          remove.mutate(r.id);
                        }}
                        disabled={remove.isPending}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="mt-4">
        <CardHeader
          title="Set a deadline"
          subtitle={
            'Leave a field blank to mean "any". Setting one that already exists for the ' +
            'same scope replaces it.'
          }
        />
        {error && (
          <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Job"
            options={[
              { value: '', label: 'Every job' },
              ...DELIVERY_SERVICE_TYPES.map((t) => ({ value: t, label: t })),
            ]}
            value={form.serviceType}
            onChange={set('serviceType')}
            error={fieldErrors['serviceType']}
          />

          <Input
            label="Hours"
            type="number"
            min={1}
            max={8760}
            inputMode="numeric"
            value={form.hours}
            onChange={set('hours')}
            error={fieldErrors['hours']}
            hint={form.hours ? openForLabel(Number(form.hours)) : 'Whole hours, up to a year'}
            required
          />

          <Select
            label="Country"
            options={[
              { value: '', label: 'Everywhere' },
              ...(business?.countryCode
                ? [
                    {
                      value: business.countryCode,
                      label: findCountry(business.countryCode)?.name ?? business.countryCode,
                    },
                  ]
                : []),
            ]}
            value={form.countryCode}
            onChange={set('countryCode')}
            error={fieldErrors['countryCode']}
          />

          <Select
            label="State"
            options={[
              { value: '', label: 'Anywhere in the country' },
              ...states.map((st) => ({ value: st.code, label: st.name })),
            ]}
            value={form.stateCode}
            onChange={set('stateCode')}
            error={fieldErrors['stateCode']}
            // A state code repeats across countries, so it needs its country.
            disabled={!form.countryCode || states.length === 0}
            {...(form.countryCode ? {} : { hint: 'Pick a country first' })}
          />

          <div className="sm:col-span-2">
            <Input
              label="Note"
              value={form.note}
              onChange={set('note')}
              error={fieldErrors['note']}
              hint="Why this deadline — for whoever reads it next"
            />
          </div>

          <div className="sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save deadline'}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}
